import type { CanvasColor } from "../../../nodes/shared/CanvasColorPalettes";

/**
 * The colour a selected tile wears — exp-canvas-toolbar's `pickInk`, mapped
 * onto the product's palettes. Every chromatic value the canvas stores derives
 * from one of the ten sticky families (`var(--sticky-<tint>)` fills,
 * `var(--spectrum-<hue>-dark)` strokes, `var(--mark-<tint>)` marks), so one
 * audited table serves notes, shape fills and pen inks alike.
 *
 * The tints cannot be their own ink: a 12% wash of a `-light` stop is
 * invisible, so each resolves to its hue's base stop, lifted where that stop
 * fails 3:1 on n-00 — butter and apricot to the `-dark` stop (too pale in
 * light), orchid, clay and mist to 90% of the base mixed with n-95 (too dark
 * in dark; the mix inverts with the ladder). Neutrals — the neutral mark, no
 * fill, the label default — return null, and null means rust.
 */
const INK_BY_TINT: Record<string, string> = {
  poppy: "var(--spectrum-red)",
  apricot: "var(--spectrum-orange-dark)",
  butter: "var(--spectrum-yellow-dark)",
  meadow: "var(--spectrum-green)",
  sea: "var(--spectrum-blue)",
  orchid: "color-mix(in oklab, var(--spectrum-violet) 90%, var(--n-95))",
  rose: "var(--spectrum-mulberry)",
  clay: "color-mix(in oklab, var(--spectrum-brown) 90%, var(--n-95))",
  mist: "color-mix(in oklab, var(--spectrum-indigo) 90%, var(--n-95))",
  oat: "var(--spectrum-olive)",
};

const TINT_BY_HUE: Record<string, string> = {
  red: "poppy",
  orange: "apricot",
  yellow: "butter",
  green: "meadow",
  blue: "sea",
  violet: "orchid",
  mulberry: "rose",
  brown: "clay",
  indigo: "mist",
  olive: "oat",
};

const STICKY = /^var\(--(?:sticky|mark)-([a-z]+)\)$/;
const SPECTRUM = /^var\(--spectrum-([a-z]+)(?:-light|-dark)?\)$/;

export function pickInk(value: string | null | undefined): string | null {
  if (!value) return null;
  const tint = STICKY.exec(value)?.[1];
  if (tint) return INK_BY_TINT[tint] ?? null;
  const hue = SPECTRUM.exec(value)?.[1];
  if (hue) return INK_BY_TINT[TINT_BY_HUE[hue] ?? ""] ?? null;
  return null;
}

/**
 * The pick's name for the tooltip and accessible name ("Sticky note ·
 * Butter"). Colour is never the sole carrier — but a neutral pick has no hue
 * to wear, so it has no name to say either.
 */
export function pickName(
  value: string | null | undefined,
  palette: CanvasColor[],
): string | null {
  if (!pickInk(value)) return null;
  return palette.find((c) => c.value === value)?.name ?? null;
}
